import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, X } from 'lucide-react';

interface Message {
  type: 'user' | 'ai';
  content: string;
  isTyping?: boolean;
}

export function DragonAiChat() {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [currentTypingIndex, setCurrentTypingIndex] = useState(-1);
  const [displayedText, setDisplayedText] = useState('');
  const chatWindowRef = useRef<HTMLDivElement>(null);

  const welcomeMessage = `Hi there! 🌸 I'm Drogo, your personal cycle companion! I can help you with:

• Understanding your cycle phases and symptoms 🌙
• Recommending natural remedies for cramps and PMS 💆‍♀️
• Tracking mood patterns and energy levels 📊
• Providing female health and hygiene tips 💫
• Suggesting cycle-synced workouts and nutrition 🏃‍♀️
• Answering any questions about your cycle! 💕

How can I support you today?`;

  useEffect(() => {
    if (expanded && messages.length === 0) {
      const initialMessage: Message = {
        type: 'ai',
        content: welcomeMessage,
        isTyping: true
      };
      setMessages([initialMessage]);
      setCurrentTypingIndex(0);
      setDisplayedText('');
    }
  }, [expanded]);

  useEffect(() => {
    if (currentTypingIndex >= 0 && messages[currentTypingIndex]?.isTyping) {
      const fullText = messages[currentTypingIndex].content;
      if (displayedText.length < fullText.length) {
        const timeout = setTimeout(() => {
          setDisplayedText(fullText.slice(0, displayedText.length + 1));
          if (chatWindowRef.current) {
            chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
          }
        }, 20);
        return () => clearTimeout(timeout);
      } else {
        setMessages(prev => prev.map((msg, idx) => 
          idx === currentTypingIndex ? { ...msg, isTyping: false } : msg
        ));
        setCurrentTypingIndex(-1);
        setDisplayedText('');
      }
    }
  }, [displayedText, currentTypingIndex, messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      type: 'user',
      content: input.trim(),
      isTyping: false
    };

    const aiResponse: Message = {
      type: 'ai',
      content: getAIResponse(input),
      isTyping: true
    };

    setMessages(prev => [...prev, userMessage, aiResponse]);
    setCurrentTypingIndex(messages.length + 1);
    setInput('');
  };

  const getAIResponse = (query: string): string => {
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('cycle') || lowerQuery.includes('period')) {
      return "Based on your cycle data, you're currently in your follicular phase (Day 8). Your energy levels are rising, making this an ideal time for high-intensity workouts. Your next period is predicted in 20 days. Would you like specific phase-based recommendations?";
    }

    if (lowerQuery.includes('cramp') || lowerQuery.includes('pain')) {
      return "I understand cramps can be challenging! 💜 Here are evidence-based remedies:\n• Apply heat therapy (15-20 mins)\n• Try gentle yoga or stretching\n• Stay hydrated and avoid caffeine\n• Consider magnesium-rich foods\n• Practice deep breathing exercises\n\nWould you like more specific recommendations?";
    }

    if (lowerQuery.includes('mood') || lowerQuery.includes('emotional')) {
      return "Mood changes are completely normal during your cycle! Your hormones influence neurotransmitters like serotonin and dopamine. Current phase analysis suggests:\n• Increasing serotonin levels\n• Optimal time for social activities\n• Good decision-making window\n\nWould you like mood-supporting tips?";
    }

    if (lowerQuery.includes('symptom') || lowerQuery.includes('track')) {
      return "I notice you've logged these symptoms recently:\n• Mild cramps (decreasing)\n• Improved energy levels\n• Better sleep quality\n\nThis pattern suggests your lifestyle changes are having a positive impact. Should we review your symptom management strategies?";
    }

    if (lowerQuery.includes('exercise') || lowerQuery.includes('workout')) {
      return "For your current cycle phase (follicular), I recommend:\n• High-intensity cardio\n• Strength training\n• Dance workouts\n\nYour energy and stamina are peaking, making this an ideal time for challenging workouts. Want a customized workout plan?";
    }

    return "I'm here to support you through your cycle! 💕 I can help with:\n• Cycle tracking and predictions\n• Symptom management\n• Phase-based recommendations\n• Exercise and nutrition tips\n• Sleep and mood support\n\nWhat would you like to know more about?";
  };

  return (
    <div className="fixed bottom-24 right-6 z-50" style={{ width: "140px" }}>
      <div className={`theme-card relative overflow-hidden ${expanded ? 'w-[280px] h-[400px] -translate-x-[140px]' : ''} transition-all duration-300`}>
        {!expanded ? (
          <button
            onClick={() => setExpanded(true)}
            className="w-full mindful-timer px-2 py-1.5 rounded-lg text-primary font-medium relative group overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-purple-500/20 to-blue-500/20 animate-pulse opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-purple-500/10 to-blue-500/10 opacity-75 transition-opacity" />
            <div className="relative z-10 flex flex-col items-center justify-center">
              <span className="flex items-center gap-1 text-xs">
                <Bot className="w-3 h-3" />
                Ask Drogo
              </span>
              <span className="text-[10px] text-primary/80">Your Cycle Companion</span>
            </div>
          </button>
        ) : (
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Bot className="w-4 h-4 text-primary" />
                  <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                </div>
                <h2 className="text-sm font-semibold">Drogo</h2>
              </div>
              <button 
                onClick={() => setExpanded(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div 
              ref={chatWindowRef}
              className="flex-1 overflow-y-auto space-y-2 bg-muted/30 p-2 rounded-lg theme-scrollbar mb-3"
            >
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-2.5 rounded-lg text-sm ${
                      message.type === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                  >
                    {message.type === 'ai' && message.isTyping && index === currentTypingIndex ? (
                      <>
                        {displayedText}
                        <span className="inline-block w-1 h-4 bg-primary ml-1 animate-pulse"></span>
                      </>
                    ) : (
                      message.content.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="relative flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask Drogo..."
                className="flex-1 bg-muted/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="theme-button p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}